import * as fs from 'fs-extra';
import * as path from 'path';
import { 
  MigrationEngine, 
  Migration, 
  MigrationPlan, 
  MigrationReport, 
  MigrationOptions, 
  ProjectStructure,
  BackupData,
  MigrationError,
  ValidationError
} from './types';
import { DezzyVersionDetector } from './version-detector';
import { BackupManager } from './backup-manager';
import { MigrationRegistry } from './migration-registry';
import { ProjectAnalyzer } from './project-analyzer';
import { ClaudeMigrationEngine, ClaudeMigrationOptions } from './claude-migration-engine';
import { aiAgentMigration } from './migrations/v2-ai-agent';
import { createTailwindMigration } from './migrations/v3-tailwind-migration';
import { DezzyDirectory } from '../project/dezzy-directory';

// Read version from package.json
function getPackageVersion(): string {
  try {
    // Try multiple possible locations for package.json
    const possiblePaths = [
      // When running from source
      path.resolve(__dirname, '../../package.json'),
      // When installed globally or locally
      path.resolve(__dirname, '../package.json'),
      // When bundled in CLI
      path.resolve(__dirname, 'package.json'),
    ];

    for (const packagePath of possiblePaths) {
      try {
        if (fs.existsSync(packagePath)) {
          const packageJson = JSON.parse(fs.readFileSync(packagePath, 'utf8'));
          return packageJson.version;
        }
      } catch (err) {
        // Continue to next path
        continue;
      }
    }

    throw new Error('package.json not found in any expected location');
  } catch (error) {
    throw new Error(`Failed to read package version: ${error}`);
  }
}

export class DezzyMigrationEngine implements MigrationEngine {
  private versionDetector: DezzyVersionDetector;
  private backupManager: BackupManager;
  private registry: MigrationRegistry;
  private analyzer: ProjectAnalyzer;
  private claudeEngine?: ClaudeMigrationEngine;
  private dezzyDir: DezzyDirectory;
  private cliVersion: string;

  constructor(
    projectPath?: string,
    cliVersion?: string,
    versionDetector?: DezzyVersionDetector,
    backupManager?: BackupManager,
    registry?: MigrationRegistry,
    analyzer?: ProjectAnalyzer,
    skipDefaultMigrations?: boolean,
    claudeOptions?: ClaudeMigrationOptions
  ) {
    this.dezzyDir = new DezzyDirectory(projectPath);
    this.cliVersion = cliVersion || getPackageVersion();
    this.versionDetector = versionDetector || new DezzyVersionDetector();
    this.backupManager = backupManager || new BackupManager();
    this.registry = registry || new MigrationRegistry();
    this.analyzer = analyzer || new ProjectAnalyzer();
    
    // Initialize Claude engine if options provided
    if (claudeOptions) {
      this.claudeEngine = new ClaudeMigrationEngine(claudeOptions);
    }
    
    // Register default migrations unless skipped (for testing)
    if (!skipDefaultMigrations) {
      // Register synchronous migrations immediately
      this.registerSyncDefaultMigrations();
      // Register async migrations in the background
      this.registerAsyncDefaultMigrations().catch(error => {
        console.warn('Failed to register async migrations:', error);
      });
    }
  }

  async detectVersion(projectPath: string): Promise<string> {
    const version = await this.versionDetector.detectVersion(projectPath);
    if (!version) {
      throw new ValidationError('Could not detect project version', [
        'Project may not be a valid Dezzy project',
        'package.json may be missing or corrupted',
        'No Dezzy-specific files found'
      ], [
        'Ensure project has package.json with dezzy dependency',
        'Check project structure follows Dezzy conventions'
      ]);
    }
    return version;
  }

  async planMigration(fromVersion: string, toVersion: string): Promise<MigrationPlan> {
    // Ensure all migrations are registered
    await this.ensureDefaultMigrationsRegistered();
    const migrations = this.registry.getPath(fromVersion, toVersion) || [];
    
    if (migrations.length === 0) {
      // Check if we're already at target version or newer
      if (this.compareVersions(fromVersion, toVersion) >= 0) {
        return {
          fromVersion,
          toVersion,
          migrations: [],
          estimatedTime: 0,
          breakingChanges: false,
          dependencies: { add: [], remove: [], update: [] }
        };
      }
      
      throw new ValidationError(`No migration path found from ${fromVersion} to ${toVersion}`, [
        'Target version may not exist',
        'Migration path may not be implemented',
        'Version may be too old or too new'
      ], [
        'Check available versions with --list-versions',
        'Consider migrating incrementally through intermediate versions'
      ]);
    }

    // Calculate total estimated time
    const estimatedTime = migrations.reduce((total, migration) => {
      return total + (migration.estimateTime ? 30000 : 30000); // Default 30s if no estimate
    }, 0);

    // Check for breaking changes
    const breakingChanges = migrations.some(m => m.breaking);

    // Aggregate dependency changes
    const dependencies = this.aggregateDependencyChanges(migrations);

    return {
      fromVersion,
      toVersion,
      migrations,
      estimatedTime,
      breakingChanges,
      dependencies
    };
  }

  async executeMigration(
    plan: MigrationPlan, 
    projectPath: string, 
    options: MigrationOptions = {}
  ): Promise<MigrationReport> {
    const startTime = new Date();
    const report: MigrationReport = {
      success: false,
      plan,
      startTime,
      completedMigrations: [],
      changes: [],
      rollbackAvailable: false
    };

    try {
      // Validate project before migration
      const validation = await this.versionDetector.validateProject(projectPath);
      if (!validation.valid) {
        throw new ValidationError('Project validation failed', validation.errors || [], validation.suggestions);
      }

      // Analyze project structure
      const projectFromAnalyzer = await this.analyzer.analyzeStructure(projectPath);
      const project = { ...projectFromAnalyzer }; // Create a mutable copy

      // Execute dry run if requested (skip backup for dry runs)
      if (options.dryRun) {
        return await this.executeDryRun(plan, project, report);
      }

      // Create backup unless explicitly skipped
      let backup: BackupData | undefined;
      if (!options.skipBackup) {
        backup = await this.backupManager.create(projectPath, {
          migrationPlan: plan,
          options
        });
        report.backup = backup;
        report.rollbackAvailable = true;
      }

      // Execute migrations in sequence
      for (let i = 0; i < plan.migrations.length; i++) {
        const migration = plan.migrations[i];
        
        if (options.verbose) {
          console.log(`Executing migration: ${migration.description}`);
        }

        try {
          // Validate migration prerequisites
          if (migration.validate) {
            const validation = await migration.validate(project);
            if (!validation.valid) {
              throw new ValidationError(
                `Migration ${migration.id} validation failed`,
                validation.errors || [],
                validation.suggestions
              );
            }
          }

          // Execute migration transformation
          const result = await migration.transform(project);
          
          if (!result.success) {
            throw new MigrationError(
              `Migration ${migration.id} failed: ${result.errors?.join(', ')}`,
              migration.id,
              i + 1,
              backup !== undefined
            );
          }

          // Apply changes to filesystem
          await this.applyChanges(result.changes, projectPath);
          
          // Update project structure for next migration
          project.version = migration.version;
          
          // Record completed migration
          report.completedMigrations!.push(migration.id);
          report.changes!.push(...result.changes);
          
          if (options.verbose && result.warnings) {
            console.warn('Migration warnings:', result.warnings.join(', '));
          }
          
        } catch (error) {
          report.failedMigration = {
            id: migration.id,
            error: error instanceof Error ? error.message : 'Unknown error',
            step: i + 1
          };
          
          // Attempt rollback if backup exists
          if (backup && !options.force) {
            if (options.verbose) {
              console.log('Migration failed, rolling back...');
            }
            await this.performRollback(project, backup);
          }
          
          throw error;
        }
      }

      // Update project metadata
      await this.updateProjectMetadata(projectPath, plan.toVersion, plan.migrations);

      // Finalize report
      report.success = true;
      report.endTime = new Date();
      report.duration = report.endTime.getTime() - startTime.getTime();

      if (options.verbose) {
        console.log(`Migration completed in ${report.duration}ms`);
      }

    } catch (error) {
      report.success = false;
      report.endTime = new Date();
      report.duration = report.endTime.getTime() - startTime.getTime();
      
      if (!(error instanceof MigrationError || error instanceof ValidationError)) {
        // Wrap unexpected errors
        throw new MigrationError(
          `Migration failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          'unknown',
          0,
          report.rollbackAvailable
        );
      }
      
      throw error;
    }

    return report;
  }

  async rollback(projectPath: string, backupId?: string): Promise<boolean> {
    try {
      const backups = await this.backupManager.list(projectPath);
      
      let backup: BackupData;
      if (backupId) {
        const foundBackup = backups.find(b => b.id === backupId);
        if (!foundBackup) {
          throw new Error(`Backup ${backupId} not found`);
        }
        backup = foundBackup;
      } else {
        // Use most recent backup
        if (backups.length === 0) {
          throw new Error('No backups available for rollback');
        }
        backup = backups.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())[0];
      }

      await this.backupManager.restore(backup, projectPath);
      return true;
    } catch (error) {
      console.error('Rollback failed:', error);
      return false;
    }
  }

  async listBackups(projectPath: string): Promise<BackupData[]> {
    return this.backupManager.list(projectPath);
  }

  async cleanupBackups(projectPath: string, keepCount: number = 5): Promise<void> {
    await this.backupManager.cleanup(projectPath, keepCount);
  }

  /**
   * Ensure all default migrations are registered (including async ones)
   */
  async ensureDefaultMigrationsRegistered(): Promise<void> {
    await this.registerAsyncDefaultMigrations();
  }

  private registerSyncDefaultMigrations(): void {
    // Register synchronous migrations
    try {
      this.registry.register(aiAgentMigration);
    } catch (error) {
      console.warn('Failed to register sync default migrations:', error);
    }
  }

  private async registerAsyncDefaultMigrations(): Promise<void> {
    // Register asynchronous migrations
    try {
      // Create and register the tailwind migration
      const tailwindMigration = await createTailwindMigration();
      this.registry.register(tailwindMigration);
    } catch (error) {
      console.warn('Failed to register async default migrations:', error);
    }
  }

  private compareVersions(version1: string, version2: string): number {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);
    
    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;
      
      if (v1Part > v2Part) return 1;
      if (v1Part < v2Part) return -1;
    }
    
    return 0;
  }

  private aggregateDependencyChanges(migrations: Migration[]): {
    add: string[];
    remove: string[];
    update: Array<{ name: string; from: string; to: string }>;
  } {
    // This would be implemented based on migration analysis
    // For now, return AI agent dependencies for v2.0.0
    return {
      add: ['@tanstack/react-query', 'zustand'],
      remove: [],
      update: []
    };
  }

  private async executeDryRun(
    plan: MigrationPlan, 
    project: ProjectStructure, 
    report: MigrationReport
  ): Promise<MigrationReport> {
    // Execute migrations without applying changes
    for (const migration of plan.migrations) {
      try {
        if (migration.validate) {
          const validation = await migration.validate(project);
          if (!validation.valid) {
            report.failedMigration = {
              id: migration.id,
              error: `Validation failed: ${validation.errors?.join(', ')}`,
              step: 0
            };
            return report;
          }
        }

        const result = await migration.transform(project);
        if (result.success) {
          report.completedMigrations!.push(migration.id);
          report.changes!.push(...result.changes);
        } else {
          report.failedMigration = {
            id: migration.id,
            error: result.errors?.join(', ') || 'Unknown error',
            step: 0
          };
          return report;
        }
      } catch (error) {
        report.failedMigration = {
          id: migration.id,
          error: error instanceof Error ? error.message : 'Unknown error',
          step: 0
        };
        return report;
      }
    }

    report.success = true;
    report.endTime = new Date();
    report.duration = report.endTime.getTime() - (report.startTime?.getTime() || 0);
    
    return report;
  }

  private async applyChanges(changes: any[], projectPath: string): Promise<void> {
    for (const change of changes) {
      const fullPath = path.join(projectPath, change.path);
      
      switch (change.type) {
        case 'create':
          await fs.ensureDir(path.dirname(fullPath));
          await fs.writeFile(fullPath, change.newContent);
          break;
          
        case 'update':
          await fs.writeFile(fullPath, change.newContent);
          break;
          
        case 'delete':
          if (await fs.pathExists(fullPath)) {
            await fs.remove(fullPath);
          }
          break;
          
        case 'move':
          if (change.oldPath) {
            const oldFullPath = path.join(projectPath, change.oldPath);
            if (await fs.pathExists(oldFullPath)) {
              await fs.ensureDir(path.dirname(fullPath));
              await fs.move(oldFullPath, fullPath);
            }
          }
          break;
      }
    }
  }

  private async performRollback(project: ProjectStructure, backup: BackupData): Promise<void> {
    try {
      await this.backupManager.restore(backup, project.path);
    } catch (error) {
      throw new MigrationError(
        `Migration failed and rollback also failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'rollback',
        0,
        false
      );
    }
  }

  private async updateProjectMetadata(
    projectPath: string, 
    newVersion: string, 
    migrations: Migration[]
  ): Promise<void> {
    const metadataDir = path.join(projectPath, '.dezzy');
    const migrationsFile = path.join(metadataDir, 'migrations.json');
    
    await fs.ensureDir(metadataDir);
    
    let history = { version: newVersion, migrations: [] as any[] };
    if (await fs.pathExists(migrationsFile)) {
      history = await fs.readJson(migrationsFile);
    }
    
    // Update version
    history.version = newVersion;
    
    // Add completed migrations to history
    migrations.forEach(migration => {
      history.migrations.push({
        id: migration.id,
        version: migration.version,
        timestamp: new Date().toISOString(),
        description: migration.description
      });
    });
    
    await fs.writeJson(migrationsFile, history, { spaces: 2 });
  }

  // Public utility methods
  async checkMigrationStatus(projectPath: string): Promise<{
    currentVersion: string;
    latestVersion: string;
    migrationsAvailable: boolean;
    lastMigration?: any;
  }> {
    const currentVersion = await this.detectVersion(projectPath);
    const latestVersion = this.getLatestVersion();
    const metadata = await this.versionDetector.extractMetadata(projectPath);
    
    return {
      currentVersion,
      latestVersion,
      migrationsAvailable: this.compareVersions(currentVersion, latestVersion) < 0,
      lastMigration: metadata.lastMigration
    };
  }

  getLatestVersion(): string {
    // Get latest version from registry
    const allMigrations = this.registry.list() || [];
    const versions = allMigrations.map(m => m.version).sort((a, b) => this.compareVersions(b, a));
    return versions[0] || '2.0.0';
  }

  listAvailableVersions(): string[] {
    return (this.registry.list() || [])
      .map(m => m.version)
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .sort((a, b) => this.compareVersions(a, b));
  }

  /**
   * Enable Claude Code-powered migrations
   */
  enableClaudeIntegration(options: ClaudeMigrationOptions): void {
    this.claudeEngine = new ClaudeMigrationEngine(options);
  }

  /**
   * Check if Claude integration is available
   */
  hasClaudeIntegration(): boolean {
    return Boolean(this.claudeEngine);
  }

  /**
   * Load Claude-powered migration instructions from directory
   */
  async loadClaudeMigrations(instructionsDir: string): Promise<void> {
    if (!this.claudeEngine) {
      throw new Error('Claude integration not enabled. Call enableClaudeIntegration() first.');
    }

    const indexPath = path.join(instructionsDir, 'index.json');
    if (!(await fs.pathExists(indexPath))) {
      throw new Error(`No migration index found at ${indexPath}`);
    }

    const index = await fs.readJson(indexPath);
    
    for (const instructionMeta of index.instructions) {
      const instructionPath = path.join(instructionsDir, `${instructionMeta.id}.json`);
      if (await fs.pathExists(instructionPath)) {
        const instruction = await fs.readJson(instructionPath);
        const migration = this.claudeEngine.createMigration(instruction);
        this.registry.register(migration);
      }
    }

    console.log(`Loaded ${index.instructions.length} Claude-powered migrations for version ${index.version}`);
  }

  /**
   * Execute a specific Claude migration by instruction
   */
  async executeClaudeMigration(
    projectPath: string, 
    instructionPath: string, 
    options: MigrationOptions = {}
  ): Promise<MigrationReport> {
    if (!this.claudeEngine) {
      throw new Error('Claude integration not enabled. Call enableClaudeIntegration() first.');
    }

    // Load instruction
    const instruction = await fs.readJson(instructionPath);
    const migration = this.claudeEngine.createMigration(instruction);

    // Analyze project
    const project = await this.analyzer.analyzeStructure(projectPath);
    
    // Create migration plan with single Claude migration
    const plan: MigrationPlan = {
      fromVersion: project.version,
      toVersion: instruction.version,
      migrations: [migration],
      estimatedTime: migration.estimateTime ? migration.estimateTime(project) : 120000,
      breakingChanges: migration.breaking,
      dependencies: instruction.dependencies || { add: [], remove: [], update: [] }
    };

    // Execute migration
    return this.executeMigration(plan, projectPath, options);
  }

  /**
   * Generate migration instructions using Claude for a specific version
   */
  async generateMigrationInstructions(
    targetVersion: string,
    changes: any,
    outputDir: string
  ): Promise<string[]> {
    const { MigrationInstructionGenerator } = await import('./migration-instruction-generator');
    
    const generator = new MigrationInstructionGenerator();
    await generator.createDefaultTemplates();

    const config = {
      fromVersion: 'current',
      toVersion: targetVersion,
      changes
    };

    const instructions = await generator.generateInstructions(config);
    await generator.saveInstructions(instructions, outputDir);

    return instructions.map(i => i.id);
  }

  /**
   * Initialize .dezzy directory for project
   */
  async initializeProject(projectPath?: string): Promise<void> {
    if (projectPath) {
      this.dezzyDir = new DezzyDirectory(projectPath);
    }
    await this.dezzyDir.initialize(this.cliVersion);
  }

  /**
   * Detect current project version from .dezzy directory
   */
  async detectProjectVersion(): Promise<string> {
    // First check if .dezzy directory exists and has version info
    if (await this.dezzyDir.exists()) {
      const version = await this.dezzyDir.getProjectVersion();
      if (version) {
        return version;
      }
    }

    // Fallback to package.json detection for legacy projects
    const projectPath = this.dezzyDir.getProjectPath();
    try {
      return await this.detectVersion(projectPath);
    } catch (error) {
      // If all detection methods fail, assume this is a new project
      // and use the CLI version as the starting point
      return this.cliVersion;
    }
  }

  /**
   * Auto-migrate project to latest version
   */
  async autoMigrate(options?: MigrationOptions): Promise<MigrationReport> {
    const projectPath = this.dezzyDir.getProjectPath();
    
    // Initialize .dezzy if needed
    if (await this.dezzyDir.needsInitialization()) {
      await this.initializeProject();
    }

    // Detect current version
    const currentVersion = await this.detectProjectVersion();
    const targetVersion = this.cliVersion;

    console.log(`🚀 Dezzy Migration`);
    console.log(`From: ${currentVersion}`);
    console.log(`To:   ${targetVersion}`);

    // Check if already up to date
    if (this.compareVersions(currentVersion, targetVersion) === 0) {
      console.log('✅ Project is already at target version');
      return {
        success: true,
        migrationsApplied: [],
        completedMigrations: [],
        changes: [],
        duration: 0,
        targetVersion
      };
    }

    // Check if downgrade (not supported)
    if (this.compareVersions(currentVersion, targetVersion) > 0) {
      console.log('❌ Downgrading is not supported');
      return {
        success: false,
        migrationsApplied: [],
        completedMigrations: [],
        changes: [],
        duration: 0,
        targetVersion,
        error: `Cannot downgrade from ${currentVersion} to ${targetVersion}`
      };
    }

    // Generate migration plan
    let plan;
    try {
      plan = await this.planMigration(currentVersion, targetVersion);
    } catch (error) {
      // If no migration path exists, that means no migrations are needed
      if (error instanceof Error && error.message.includes('No migration path found')) {
        console.log('✅ No migrations needed');
        await this.dezzyDir.updateVersion(targetVersion, this.cliVersion);
        return {
          success: true,
          migrationsApplied: [],
          completedMigrations: [],
          changes: [],
          duration: 0,
          targetVersion
        };
      }
      throw error;
    }
    
    if (plan.migrations.length === 0) {
      console.log('✅ No migrations needed');
      return {
        success: true,
        migrationsApplied: [],
        completedMigrations: [],
        changes: [],
        duration: 0,
        targetVersion
      };
    }

    console.log(`📋 Found ${plan.migrations.length} migration(s) to apply`);

    // Execute migrations
    const startTime = Date.now();
    let appliedMigrations: string[] = [];

    try {
      for (const migration of plan.migrations) {
        console.log(`⚙️  Applying migration: ${migration.id}`);
        
        // Check if already applied
        if (await this.dezzyDir.hasMigrationBeenApplied(migration.id)) {
          console.log(`⏭️  Skipping ${migration.id} (already applied)`);
          continue;
        }

        const migrationStart = Date.now();
        
        // Create backup
        const project = await this.analyzer.analyzeStructure(projectPath);
        const backupFiles = project.files.map((f: any) => ({ path: f.path, content: f.content }));
        const backupPath = await this.dezzyDir.createBackup(`migration-${migration.id}`, backupFiles);
        
        // Apply migration
        const result = await migration.transform(project);
        
        if (result.success) {
          // Apply changes to filesystem
          await this.applyChanges(result.changes || [], projectPath);
          
          // Record successful migration
          const migrationEnd = Date.now();
          await this.dezzyDir.addMigrationRecord({
            id: migration.id,
            version: migration.version,
            appliedAt: new Date().toISOString(),
            duration: migrationEnd - migrationStart,
            success: true
          });
          
          appliedMigrations.push(migration.id);
          console.log(`✅ Applied migration: ${migration.id}`);
        } else {
          // Record failed migration
          const migrationEnd = Date.now();
          await this.dezzyDir.addMigrationRecord({
            id: migration.id,
            version: migration.version,
            appliedAt: new Date().toISOString(),
            duration: migrationEnd - migrationStart,
            success: false,
            error: result.errors?.join('; ')
          });
          
          throw new Error(`Migration ${migration.id} failed: ${result.errors?.join('; ')}`);
        }
      }

      // Update project version
      await this.dezzyDir.updateVersion(targetVersion, this.cliVersion);
      
      const duration = Date.now() - startTime;
      console.log(`✅ Migration completed successfully in ${(duration / 1000).toFixed(1)}s`);
      
      return {
        success: true,
        migrationsApplied: appliedMigrations,
        completedMigrations: appliedMigrations,
        changes: [],
        duration,
        targetVersion
      };

    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      console.log(`❌ Migration failed: ${errorMessage}`);
      
      return {
        success: false,
        migrationsApplied: appliedMigrations,
        completedMigrations: appliedMigrations,
        changes: [],
        duration,
        targetVersion,
        error: errorMessage
      };
    }
  }

  /**
   * Get migration status for current project
   */
  async getProjectMigrationStatus(): Promise<{
    currentVersion: string;
    latestVersion: string;
    needsMigration: boolean;
    availableMigrations: Migration[];
    appliedMigrations: string[];
  }> {
    const currentVersion = await this.detectProjectVersion();
    const latestVersion = this.cliVersion;
    
    let plan;
    try {
      plan = await this.planMigration(currentVersion, latestVersion);
    } catch (error) {
      // If no migration path exists, no migrations are available
      if (error instanceof Error && error.message.includes('No migration path found')) {
        plan = { migrations: [], estimatedTime: 0, breakingChanges: false };
      } else {
        throw error;
      }
    }
    
    const history = await this.dezzyDir.getMigrationHistory();
    const appliedMigrations = history
      .filter(record => record.success)
      .map(record => record.id);

    return {
      currentVersion,
      latestVersion,
      needsMigration: plan.migrations.length > 0,
      availableMigrations: plan.migrations,
      appliedMigrations
    };
  }
}